import React from 'react';
import { motion } from 'motion/react';
import { Target, Lightbulb, Heart, Globe, MessageCircle } from 'lucide-react';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import { fadeInUp, hoverLift } from '../../lib/motion';

interface Value {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const About: React.FC = () => {
  const handleTalkToTeam = useWhatsAppDemo('Ciao, vorrei saperne di più su ChatLy');

  const values: Value[] = [
    {
      icon: Lightbulb,
      title: 'Innovazione',
      description: 'Sviluppiamo soluzioni all\'avanguardia per automatizzare e migliorare le comunicazioni aziendali.'
    },
    {
      icon: Heart,
      title: 'Customer-First',
      description: 'Il successo dei nostri clienti è la nostra priorità. Ogni funzionalità è progettata pensando alle loro esigenze.'
    },
    {
      icon: Target,
      title: 'Risultati Concreti',
      description: 'Non promettiamo miracoli, ma risultati misurabili che trasformano realmente il tuo business.'
    },
    {
      icon: Globe,
      title: 'Accessibilità',
      description: 'Rendiamo l\'AI e l\'automazione accessibili a tutte le aziende, dalle micro-imprese alle enterprise.'
    }
  ];

  return (
    <section id="about" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Chi{' '}
            <span className="text-primary">
              Siamo
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Siamo un team di innovatori italiani che crede nel potere dell'automazione intelligente.
            Dal 2021 aiutiamo le aziende a trasformare il loro customer service con soluzioni AI all'avanguardia.
          </p>
        </motion.div>

        {/* Mission */}
        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="bg-card rounded-3xl p-8 lg:p-12 shadow-sm border border-border mb-16 max-w-3xl mx-auto"
        >
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
            <Target className="h-7 w-7 text-primary" aria-hidden="true" />
          </div>
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-4">
            La Nostra Missione
          </h3>
          <p className="text-text-secondary text-lg leading-relaxed">
            Dal 2021 lavoriamo per democratizzare l'intelligenza artificiale conversazionale,
            rendendo l'automazione WhatsApp accessibile e vantaggiosa per ogni tipo di business,
            dalla pizzeria sotto casa alla multinazionale.
          </p>
        </motion.div>

        {/* Values */}
        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary text-center mb-12">
            I Nostri Valori
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                {...fadeInUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -6 }}
                className="bg-card rounded-2xl p-6 shadow-sm border border-border text-center hover:shadow-md transition-shadow duration-200"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
                <h4 className="text-xl font-bold text-text-primary mb-3">
                  {value.title}
                </h4>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-8 lg:p-12 text-white">
            <h3 className="text-2xl lg:text-3xl font-heading font-bold mb-4">
              Vuoi far parte della rivoluzione dell'automazione?
            </h3>
            <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
              Unisciti a centinaia di aziende che hanno già trasformato il loro customer service con ChatLy.
            </p>
            <motion.button
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              transition={hoverLift}
              onClick={handleTalkToTeam}
              className="inline-flex items-center gap-2 bg-white text-primary font-bold py-4 px-8 rounded-full text-lg shadow-md hover:shadow-lg transition-shadow duration-200"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Parla con il nostro team
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
